import { computed, h, type MaybeRef, ref, unref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import {
  ProfileApi,
  type CategoryResponse,
  type EventResponse,
  type TeamResponse,
  type TournamentResponse,
  type SubscriptionResponse,
  type ProfilesubscriptionPatchRequest,
} from "@xs2event/js-lib/api";
import { useToast } from "@xs2event/js-lib/toast";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/store/user";

const profileApi = new ProfileApi();
const WATCHLIST_MANAGE_URL = "/user/watchlist";

export type WatchlistEntry = SubscriptionResponse;

const WATCHLIST_QUERY_KEY = ["watchlist"];

const getSubscriptionType = (options?: UseSubscriptionOptions) => {
  if (!options) return "event";
  if ("team" in options) return "team";
  if ("tournament" in options) return "tournament";
  if ("category" in options && options.category) return "category";
  return "event";
};

export const useWatchlistQuery = () => {
  const userStore = useUserStore();
  return useQuery<WatchlistEntry[]>({
    queryKey: WATCHLIST_QUERY_KEY,
    queryFn: async () => {
      const res = await profileApi.subscriptionsGet({ pageSize: 250 });
      return res.subscriptions ?? [];
    },
    staleTime: 1000 * 60 * 5,
    enabled: computed(() => !!userStore.isUser),
  });
};

export const useWatchlistPatchMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ProfilesubscriptionPatchRequest) => {
      await profileApi.profilesubscriptionPatch(payload);
    },
    onSuccess: () => {
      const toast = useToast();
      queryClient.invalidateQueries({ queryKey: WATCHLIST_QUERY_KEY });
      toast.addToast({ title: "Success", content: "Notifications updated successfully", duration: 3000 });
    },
  });
};

export type UseSubscriptionOptions =
  | { event: MaybeRef<EventResponse | undefined>; category?: never; team?: never; tournament?: never }
  | {
      event: MaybeRef<EventResponse | undefined>;
      category: MaybeRef<CategoryResponse | undefined>;
      team?: never;
      tournament?: never;
    }
  | { team: MaybeRef<TeamResponse | undefined>; event?: never; category?: never; tournament?: never }
  | { tournament: MaybeRef<TournamentResponse | undefined>; event?: never; category?: never; team?: never };

export const useSubscriptions = (options?: UseSubscriptionOptions) => {
  const queryClient = useQueryClient();
  const { data: items, isLoading: isFetching } = useWatchlistQuery();
  const toast = useToast();
  const { t } = useI18n();

  const eventId = computed(() => (options && "event" in options ? unref(options.event)?.eventId : undefined));
  const categoryId = computed(() =>
    options && "category" in options ? unref(options.category)?.categoryId : undefined
  );
  const teamId = computed(() => (options && "team" in options ? unref(options.team)?.teamId : undefined));
  const tournamentId = computed(() =>
    options && "tournament" in options ? unref(options.tournament)?.tournamentId : undefined
  );

  const matchedSubscription = computed<WatchlistEntry | undefined>(() => {
    if (!items.value || !options) return undefined;

    if (teamId.value) {
      return items.value.find((item) => !!teamId.value && item.teamId === teamId.value);
    }

    if (tournamentId.value) {
      return items.value.find((item) => !!tournamentId.value && item.tournamentId === tournamentId.value);
    }

    if (categoryId.value) {
      return items.value.find((item) => item.eventId === eventId.value && item.categoryId === categoryId.value);
    }

    if (eventId.value) {
      return items.value.find((item) => item.eventId === eventId.value && !item.categoryId);
    }

    return undefined;
  });

  const localOverride = ref<boolean>();

  const isSubscribed = computed(() =>
    localOverride.value != null ? localOverride.value : !!matchedSubscription.value
  );

  const refetch = async () => {
    await queryClient.refetchQueries({ queryKey: WATCHLIST_QUERY_KEY });
    localOverride.value = undefined;
  };

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      if (!options) throw new Error("useSubscription: no valid subscription options provided");

      if ("team" in options && options.team) {
        if (!teamId.value) throw new Error("useSubscription: team is required");
        return profileApi.subscriptionsPost({ subscriptionRequestPost: { teamId: teamId.value } });
      }

      if ("tournament" in options && options.tournament) {
        if (!tournamentId.value) throw new Error("useSubscription: tournament is required");
        return profileApi.subscriptionsPost({ subscriptionRequestPost: { tournamentId: tournamentId.value } });
      }

      if ("category" in options && options.category) {
        if (!eventId.value) throw new Error("useSubscription: event is required");
        return profileApi.subscriptionsPost({
          subscriptionRequestPost: { eventId: eventId.value, categoryId: categoryId.value },
        });
      }

      if ("event" in options && options.event) {
        if (!eventId.value) throw new Error("useSubscription: event is required");
        return profileApi.subscriptionsPost({ subscriptionRequestPost: { eventId: eventId.value } });
      }

      throw new Error("useSubscription: no valid subscription options provided");
    },
    onMutate: () => {
      localOverride.value = true;
    },
    onSuccess: () => {
      const type = getSubscriptionType(options);
      const subscribedMessage = t("You will receive notifications about updates for this {type}.", { type }).replace(
        "{type}",
        type
      );
      toast.addToast({
        title: t("Subscribed!"),
        content: h("div", { class: "flex flex-col gap-1" }, [
          h("span", subscribedMessage),
          h("a", { href: WATCHLIST_MANAGE_URL, class: "underline", target: "_blank", rel: "noopener noreferrer" }, [
            t("Manage these alerts in Event Alerts"),
          ]),
        ]),
        status: "success",
        duration: 4000,
      });
      return refetch();
    },
    onError: () => {
      localOverride.value = undefined;
    },
  });

  const unsubscribeMutation = useMutation({
    mutationFn: async () => {
      const sub = matchedSubscription.value;
      if (!sub?.subscriptionId) throw new Error("useSubscription: no active subscription to remove");
      return profileApi.profilesubscriptionDelete({ watchlistId: sub.subscriptionId });
    },
    onMutate: () => {
      localOverride.value = false;
    },
    onSuccess: async () => {
      const type = getSubscriptionType(options);
      const unsubscribedMessage = t("You will no longer receive notifications about this {type}.", { type }).replace(
        "{type}",
        type
      );
      toast.addToast({
        title: t("Unsubscribed"),
        content: h("div", { class: "flex flex-col gap-1" }, [
          h("span", unsubscribedMessage),
          h("a", { href: WATCHLIST_MANAGE_URL, class: "underline", target: "_blank", rel: "noopener noreferrer" }, [
            t("Manage these alerts in Event Alerts"),
          ]),
        ]),
        status: "success",
        duration: 4000,
      });
      await refetch();
    },
    onError: () => {
      localOverride.value = undefined;
    },
  });

  const subscribe = () => subscribeMutation.mutateAsync();
  const unsubscribe = () => unsubscribeMutation.mutateAsync();
  const toggleSubscribe = async () => (isSubscribed.value ? await unsubscribe() : await subscribe());

  const isLoading = computed(
    () => isFetching.value || subscribeMutation.isPending.value || unsubscribeMutation.isPending.value
  );

  return { subscriptions: items, isSubscribed, isLoading, subscribe, unsubscribe, toggleSubscribe };
};
